import { mailEvents } from './mailEvents';
import { useEmailStore } from '@/stores/emailStore';
import { useSenderGroupingStore } from '@/stores/senderGroupingStore';
import { useAccountStore } from '@/stores/accountStore';
import { useFolderStore } from '@/stores/folderStore';
import { useBulkOperationsStore } from '@/stores/bulkOperationsStore';
import { syncContactStateAcrossStores } from '@/stores/contactStateSync';
import { useContactStore } from '@/stores/contactStore';
// Shared guard so BOTH the enqueue side (here) and the drain side
// (flushPendingMailboxReload) apply the same ownership check.
import { shouldDeferMailboxReload } from '@/stores/mailboxReloadGuard';

// Store cleanup functions for HMR
const cleanupFunctions: Array<() => void> = [];

cleanupFunctions.push(
  mailEvents.on('mail:selection:clear', () => {
    useEmailStore.getState().clearSelection();
    useBulkOperationsStore.getState().clearSelection();
  })
);

cleanupFunctions.push(
  mailEvents.on('mail:senderGrouping:reset', () => {
    useSenderGroupingStore.getState().resetSenderGroupingState('external reset');
  })
);

cleanupFunctions.push(
  mailEvents.on('mail:mailbox:reload', ({ accountId, folderId, options }) => {
    if (!accountId || !folderId) {
      return;
    }

    // Validate that the reload is for the currently selected account/folder
    const currentAccountId = useAccountStore.getState().selectedAccountId;
    const currentFolderId = useFolderStore.getState().selectedFolderId();

    if (currentAccountId !== accountId || currentFolderId !== folderId) {
      // User has switched to a different account/folder, ignore this stale reload
      return;
    }

    const emailStore = useEmailStore.getState();

    const reloadOptions = options ?? { reset: true };

    const senderState = useSenderGroupingStore.getState();
    const shouldDefer = shouldDeferMailboxReload();
    console.log('[mail:mailbox:reload] Reload requested', {
      accountId,
      folderId,
      shouldDefer,
      pendingGroupActions: Object.keys(senderState.pendingGroupActions),
      activeSenderGroup: senderState.activeSenderGroup?.key ?? null,
    });

    if (shouldDefer) {
      console.log('[mail:mailbox:reload] Deferring reload due to pending group actions or active sender group');
      useSenderGroupingStore.setState({
        pendingMailboxReload: {
          accountId,
          folderId,
          options: reloadOptions,
        },
      });
      return;
    }
    const skipReloadConfig = useSenderGroupingStore.getState().skipNextMailboxReloadFor;
    if (skipReloadConfig) {
      const isExpired = skipReloadConfig.expiresAt <= Date.now();
      console.log('[mail:mailbox:reload] Checking skip config', {
        skipConfig: skipReloadConfig,
        isExpired,
        matches: skipReloadConfig.accountId === accountId && skipReloadConfig.folderId === folderId,
      });
      if (isExpired) {
        useSenderGroupingStore.setState({ skipNextMailboxReloadFor: null });
      } else if (
        skipReloadConfig.accountId === accountId &&
        skipReloadConfig.folderId === folderId
      ) {
        console.log('[mail:mailbox:reload] Skipping reload due to recent archive/delete operation');
        useSenderGroupingStore.setState({ skipNextMailboxReloadFor: null });
        return;
      }
    }

    if (emailStore.emailsLoading) {
      useSenderGroupingStore.setState({
        pendingMailboxReload: {
          accountId,
          folderId,
          options: reloadOptions,
        },
      });
      return;
    }

    useSenderGroupingStore.setState({ pendingMailboxReload: null });
    void emailStore
      .loadEmails(accountId, folderId, {
        ...reloadOptions,
      })
      .catch((error) => {
        console.error('Failed to reload mailbox after realtime event', error);
      });
  })
);

cleanupFunctions.push(
  mailEvents.on('mail:contact:stateChanged', ({ contactId, approvedState, notificationsEnabled }) => {
    syncContactStateAcrossStores(contactId, approvedState, { notificationsEnabled });
  })
);

cleanupFunctions.push(
  mailEvents.on('mail:contact:reviewUpdated', ({ contactId, recommendation }) => {
    // Convert messageId from null to undefined to match Contact schema
    const normalizedRec = recommendation ? {
      ...recommendation,
      messageId: recommendation.messageId ?? undefined,
    } : null;
    useContactStore.getState().updateContactReviewRecommendation(contactId, normalizedRec);
  })
);

cleanupFunctions.push(
  mailEvents.on('mail:thread:refresh', ({ threadId, accountId }) => {
    if (!threadId || !accountId) {
      return;
    }
    const emailStore = useEmailStore.getState();
    // Only refresh if we're currently viewing this thread — and refresh the
    // data IN PLACE (no remount/spinner, no selection hijack to the newest
    // message, no history entry): this is a data update, not a navigation.
    if (emailStore.selectedThreadId === threadId) {
      void emailStore.refreshSelectedThread(threadId, accountId);
    }
  })
);

// Clean up event listeners during HMR
if (import.meta.hot) {
  import.meta.hot.dispose(() => {
    cleanupFunctions.forEach((cleanup) => cleanup());
  });
}
